import React, { useState, useCallback } from 'react';
import SeverityBadge from '../components/SeverityBadge';
import useAPI from '../hooks/useAPI';
import { getWeeklyTrends, refreshWeeklyTrends } from '../services/api';

const COUNTRY_OPTIONS = [
  { key: 'ALL', label: 'All' },
  { key: 'Israel', label: 'Israel' },
  { key: 'India', label: 'India' },
];

export default function ErrorsTrend() {
  const [countryFilter, setCountryFilter] = useState('ALL');

  return (
    <div>
      <h1 style={styles.title}>Errors Trend</h1>
      <p style={styles.subtitle}>
        Rising and improving error codes over the last weeks &mdash; errors per day, weather-adjusted.
      </p>

      {/* Region filter */}
      <div style={styles.filterBar}>
        <span style={styles.filterLabel}>Region:</span>
        <div style={styles.countryGroup}>
          {COUNTRY_OPTIONS.map((opt, i, arr) => {
            const active = countryFilter === opt.key;
            return (
              <button
                key={opt.key}
                onClick={() => setCountryFilter(opt.key)}
                style={{
                  ...styles.countryBtn,
                  ...(active ? styles.countryBtnActive : {}),
                  borderTopLeftRadius: i === 0 ? 6 : 0,
                  borderBottomLeftRadius: i === 0 ? 6 : 0,
                  borderTopRightRadius: i === arr.length - 1 ? 6 : 0,
                  borderBottomRightRadius: i === arr.length - 1 ? 6 : 0,
                  borderLeft: i === 0 ? '1px solid #dfe6e9' : 'none',
                }}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>

      <WeeklyTrendsSection countryFilter={countryFilter} />
    </div>
  );
}

// =========================================================================
// Weekly Trends section (moved from Fleet Overview)
// =========================================================================

function WeeklyTrendsSection({ countryFilter }) {
  const { data, loading, reload } = useAPI(getWeeklyTrends);
  const [refreshing, setRefreshing] = useState(false);
  const [threshold, setThreshold] = useState(25);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await refreshWeeklyTrends();
      await reload();
    } catch (e) {
      console.error(e);
    } finally {
      setRefreshing(false);
    }
  }, [reload]);

  const generatedAt = data?.generated_at
    ? new Date(data.generated_at).toLocaleString()
    : null;

  const applyCountry = (rows) => {
    if (countryFilter === 'ALL') return rows;
    return rows.filter(r =>
      r.country_code === countryFilter ||
      r.country === countryFilter
    );
  };

  const splitByThreshold = (rows) => {
    const risingOk = (r) => r.growth_pct >= threshold;
    const improvingOk = (r) => r.growth_pct <= -threshold;
    const rising = rows.filter(risingOk)
      .sort((a, b) => b.errors_last_7d - a.errors_last_7d);
    const improving = rows.filter(improvingOk)
      .sort((a, b) => b.errors_prior_30d - a.errors_prior_30d);
    return { rising, improving };
  };

  const country = splitByThreshold(applyCountry(data?.by_country || []));
  const project = splitByThreshold(applyCountry(data?.by_project || []));

  return (
    <div style={trendStyles.wrap}>
      <div style={trendStyles.header}>
        <div>
          <h2 style={styles.sectionTitle}>
            Weekly Trends &mdash; rising & improving error codes
          </h2>
          <p style={trendStyles.sub}>
            Errors per day — last 7 days vs 30 days prior.
            Days with &lt;50% of usual robots running (weather) are excluded from both sides.
            Refreshes weekly.
          </p>
        </div>
        <div style={trendStyles.headerRight}>
          <label style={trendStyles.thresholdLabel}>
            Threshold
            <input
              type="number"
              min={1}
              max={500}
              step={1}
              value={threshold}
              onChange={(e) => setThreshold(Math.max(1, Number(e.target.value) || 0))}
              style={trendStyles.thresholdInput}
            />
            <span style={{ color: '#636e72', marginLeft: 2 }}>%</span>
          </label>
          {generatedAt && (
            <span style={trendStyles.ts}>Updated: {generatedAt}</span>
          )}
          <button
            onClick={handleRefresh}
            disabled={refreshing || loading}
            style={trendStyles.refreshBtn}
          >
            {refreshing ? 'Refreshing...' : 'Refresh now'}
          </button>
        </div>
      </div>

      {loading && !data ? (
        <div style={trendStyles.empty}>Computing trends...</div>
      ) : (
        <>
          <TrendsScope
            scopeLabel="Country"
            scopeKey="country"
            rising={country.rising}
            improving={country.improving}
            threshold={threshold}
          />
          <TrendsScope
            scopeLabel="Project"
            scopeKey="project"
            rising={project.rising}
            improving={project.improving}
            threshold={threshold}
          />
        </>
      )}
    </div>
  );
}

function TrendsScope({ scopeLabel, scopeKey, rising, improving, threshold }) {
  const grouped = scopeKey === 'project';
  return (
    <div style={trendStyles.scopeBlock}>
      <h3 style={trendStyles.scopeTitle}>By {scopeLabel}</h3>

      <div style={trendStyles.sideBySide}>
        <TrendsTable
          title={`Rising — growth \u2265 +${threshold}%`}
          scopeKey={scopeKey}
          scopeLabel={scopeLabel}
          rows={rising}
          direction="up"
          groupByProject={grouped}
          volumeField="errors_last_7d"
        />
        <TrendsTable
          title={`Improving — growth \u2264 \u2212${threshold}%`}
          scopeKey={scopeKey}
          scopeLabel={scopeLabel}
          rows={improving}
          direction="down"
          groupByProject={grouped}
          volumeField="errors_prior_30d"
        />
      </div>
    </div>
  );
}

function TrendsTable({
  title, scopeKey, scopeLabel, rows, direction,
  groupByProject = false, volumeField = 'errors_last_7d',
}) {
  if (!rows.length) {
    return (
      <div>
        <h4 style={trendStyles.tableTitle}>{title}</h4>
        <div style={trendStyles.empty}>
          No error codes in this category at the current threshold.
        </div>
      </div>
    );
  }

  let groups;
  if (groupByProject) {
    const byProject = new Map();
    for (const r of rows) {
      const key = r.project || r[scopeKey] || '';
      if (!byProject.has(key)) byProject.set(key, []);
      byProject.get(key).push(r);
    }
    groups = Array.from(byProject.entries()).map(([name, groupRows]) => ({
      name,
      rows: [...groupRows].sort((a, b) => b[volumeField] - a[volumeField]),
      total: groupRows.reduce((s, r) => s + (r[volumeField] || 0), 0),
    }));
    groups.sort((a, b) => b.total - a.total);
  } else {
    groups = [{ name: null, rows, total: 0 }];
  }

  const volumeLabel = volumeField === 'errors_last_7d' ? 'last 7 days' : 'prior 30 days';

  return (
    <div>
      <h4 style={trendStyles.tableTitle}>{title}</h4>
      <div style={trendStyles.tableWrap}>
        <table style={trendStyles.table}>
          <colgroup>
            {!groupByProject && <col style={{ width: '18%' }} />}
            <col style={{ width: groupByProject ? '56%' : '38%' }} />
            <col style={{ width: '14%' }} />
            <col style={{ width: '14%' }} />
            <col style={{ width: '16%' }} />
          </colgroup>
          <thead>
            <tr>
              {!groupByProject && <th style={trendStyles.th}>{scopeLabel}</th>}
              <th style={trendStyles.th}>Error code</th>
              <th style={{ ...trendStyles.th, textAlign: 'right' }}>/day 7d</th>
              <th style={{ ...trendStyles.th, textAlign: 'right' }}>/day 30d prior</th>
              <th style={{ ...trendStyles.th, textAlign: 'right' }}>Growth</th>
            </tr>
          </thead>
          <tbody>
            {groups.map((g, gi) => {
              const headerRow = groupByProject ? (
                <tr key={`grp-${g.name}-${gi}`} style={trendStyles.groupHeader}>
                  <td colSpan={4} style={trendStyles.groupHeaderName}>{g.name}</td>
                  <td style={trendStyles.groupHeaderTotal}>
                    {g.total.toLocaleString()}
                    <div style={trendStyles.groupHeaderSub}>{volumeLabel}</div>
                  </td>
                </tr>
              ) : null;

              const dataRows = g.rows.map((r, i) => (
                <tr
                  key={`${r[scopeKey]}-${r.error_code}-${gi}-${i}`}
                  style={i % 2 ? trendStyles.trOdd : trendStyles.trEven}
                >
                  {!groupByProject && <td style={trendStyles.td}>{r[scopeKey]}</td>}
                  <td style={{ ...trendStyles.td, whiteSpace: 'normal', paddingLeft: groupByProject ? 16 : 4 }}>
                    <span style={{ fontWeight: 600, fontSize: 11 }}>{r.error_code}</span>
                    <span style={{ marginLeft: 6 }}>
                      <SeverityBadge severity={r.severity} />
                    </span>
                  </td>
                  <td
                    style={{ ...trendStyles.td, textAlign: 'right', fontWeight: 600 }}
                    title={`${r.days_worked_last_7d} active day${r.days_worked_last_7d === 1 ? '' : 's'} of 7 last days`}
                  >
                    {Math.round(r.rate_last_7d).toLocaleString()}
                  </td>
                  <td
                    style={{ ...trendStyles.td, textAlign: 'right' }}
                    title={`${r.days_worked_prior_30d} active day${r.days_worked_prior_30d === 1 ? '' : 's'} of 30 days`}
                  >
                    {Math.round(r.rate_prior_30d).toLocaleString()}
                  </td>
                  <td style={{ ...trendStyles.td, textAlign: 'right' }}>
                    <GrowthBadge pct={r.growth_pct} direction={direction} />
                  </td>
                </tr>
              ));

              return (
                <React.Fragment key={`g-${gi}`}>
                  {headerRow}
                  {dataRows}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function GrowthBadge({ pct, direction }) {
  const magnitude = Math.abs(pct ?? 0);
  let bg, color;
  if (direction === 'down') {
    if (magnitude >= 50) { bg = '#00b894'; color = '#fff'; }
    else if (magnitude >= 25) { bg = '#55efc4'; color = '#014e37'; }
    else { bg = '#d1f2eb'; color = '#014e37'; }
  } else {
    if (magnitude >= 100) { bg = '#d63031'; color = '#fff'; }
    else if (magnitude >= 50) { bg = '#e17055'; color = '#fff'; }
    else { bg = '#fdcb6e'; color = '#5a3e00'; }
  }
  return (
    <span style={{
      display: 'inline-block', padding: '2px 7px', borderRadius: 10,
      background: bg, color, fontWeight: 700, fontSize: 11,
    }}>
      {formatPct(pct)}
    </span>
  );
}

function formatPct(pct) {
  if (pct == null || Number.isNaN(pct)) return '-';
  const sign = pct >= 0 ? '+' : '';
  return `${sign}${Math.round(pct)}%`;
}

const styles = {
  title: { fontSize: 24, fontWeight: 700, margin: 0 },
  subtitle: { fontSize: 14, color: '#636e72', marginTop: 4, marginBottom: 16 },
  filterBar: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginBottom: 20,
  },
  filterLabel: {
    fontSize: 13,
    fontWeight: 600,
    color: '#636e72',
  },
  countryGroup: {
    display: 'inline-flex',
  },
  countryBtn: {
    padding: '6px 14px',
    border: '1px solid #dfe6e9',
    borderLeft: 'none',
    background: '#fff',
    color: '#636e72',
    cursor: 'pointer',
    fontSize: 13,
    fontFamily: 'inherit',
    fontWeight: 600,
  },
  countryBtnActive: {
    background: '#6c5ce7',
    color: '#fff',
    borderColor: '#6c5ce7',
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 600,
    marginBottom: 8,
  },
};

const trendStyles = {
  wrap: {
    background: '#fff',
    borderRadius: 12,
    padding: 20,
    marginBottom: 32,
    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 16,
    gap: 16,
    flexWrap: 'wrap',
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  sub: { fontSize: 12, color: '#636e72', margin: '4px 0 0' },
  ts: { fontSize: 12, color: '#636e72' },
  refreshBtn: {
    padding: '6px 14px',
    borderRadius: 6,
    border: '1px solid #dfe6e9',
    background: '#f5f6fa',
    cursor: 'pointer',
    fontSize: 13,
    fontFamily: 'inherit',
  },
  tableBlock: { marginTop: 12 },
  sideBySide: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 16,
    marginTop: 12,
  },
  scopeBlock: {
    marginTop: 24,
    paddingTop: 16,
    borderTop: '1px solid #ecf0f1',
  },
  scopeTitle: {
    fontSize: 15,
    fontWeight: 700,
    margin: '0 0 4px',
    color: '#2d3436',
  },
  thresholdLabel: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 13,
    color: '#636e72',
    fontWeight: 600,
  },
  thresholdInput: {
    width: 60,
    padding: '4px 8px',
    border: '1px solid #dfe6e9',
    borderRadius: 6,
    fontSize: 13,
    fontFamily: 'inherit',
    textAlign: 'right',
  },
  tableTitle: {
    fontSize: 14,
    fontWeight: 600,
    margin: '0 0 8px',
    color: '#2d3436',
  },
  tableWrap: {
    border: '1px solid #ecf0f1',
    borderRadius: 8,
    overflow: 'auto',
    maxHeight: 420,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: 11,
    tableLayout: 'fixed',
  },
  th: {
    textAlign: 'left',
    padding: '5px 4px',
    background: '#f8f9fa',
    borderBottom: '1px solid #ecf0f1',
    fontWeight: 600,
    color: '#2d3436',
    position: 'sticky',
    top: 0,
    whiteSpace: 'nowrap',
    fontSize: 11,
  },
  td: {
    padding: '5px 4px',
    borderBottom: '1px solid #f5f6fa',
    verticalAlign: 'top',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  groupHeader: { background: '#eef1fb' },
  groupHeaderName: {
    padding: '7px 10px',
    fontWeight: 700,
    fontSize: 12,
    color: '#2d3436',
    borderTop: '2px solid #d8defb',
    borderBottom: '1px solid #d8defb',
  },
  groupHeaderTotal: {
    padding: '7px 10px',
    textAlign: 'right',
    fontWeight: 700,
    fontSize: 13,
    color: '#6c5ce7',
    borderTop: '2px solid #d8defb',
    borderBottom: '1px solid #d8defb',
    whiteSpace: 'nowrap',
  },
  groupHeaderSub: {
    fontSize: 10,
    color: '#95a5a6',
    fontWeight: 500,
    marginTop: 1,
  },
  trEven: { background: '#fff' },
  trOdd: { background: '#fafbfc' },
  empty: {
    padding: 24,
    textAlign: 'center',
    color: '#95a5a6',
    background: '#fafbfc',
    borderRadius: 8,
    fontSize: 13,
  },
};
